import React, { useContext } from "react";
import { Badge, Box, Flex } from "theme-ui";
import moment from "moment-timezone";
import UserContext from "context/User";

/**
 * ONE place that decides how an invoice's status reads, everywhere it is
 * shown (invoice list, invoice page, invoice document, client page, project
 * timeline, work order, location records). The stored `status` is never
 * changed by this: it drives Zoho and receivables. This only decides the
 * words on screen.
 *
 *   Created + early-payment email out   -> "Sent"            + Early tag
 *   Paid / Partial Paid, work not done  -> "Paid" / "Partial" + Early tag
 *   Created, work done (staff only)     -> "Created"         + Ready to bill
 *
 * "Work done" = project status Complete, Invoice Sent or Closed. When the
 * project status is not known on a screen, no project-based tag is shown
 * rather than guessing.
 */

export const PROJECT_DONE_STATUSES = ["Complete", "Invoice Sent", "Closed"];

export interface InvoiceStatusDisplay {
  label: string;
  /** theme-ui Badge variant */
  variant: "warning" | "info" | "success" | "danger";
  tag?: { text: string; title: string };
}

const BASE: Record<number, Pick<InvoiceStatusDisplay, "label" | "variant">> = {
  0: { label: "Created", variant: "warning" },
  1: { label: "Sent", variant: "info" },
  2: { label: "Paid", variant: "success" },
  3: { label: "Partial Paid", variant: "warning" },
  4: { label: "Voided", variant: "danger" },
};

const fmt = (d: any) => (d ? moment(d).format("MMM D, YYYY") : "");

export function getInvoiceStatusDisplay(
  invoice: any,
  opts: { isStaff: boolean; projectStatusName?: string | null },
): InvoiceStatusDisplay {
  const status = Number(invoice?.status ?? 0);
  const base = BASE[status] ?? BASE[0];
  const projectStatusName = opts.projectStatusName ?? invoice?.project?.status?.name ?? null;
  const projectKnown = !!projectStatusName;
  const workDone = projectKnown && PROJECT_DONE_STATUSES.includes(projectStatusName as string);
  const cancelled = projectStatusName === "Cancelled";

  if (status === 0) {
    if (workDone && opts.isStaff) {
      return {
        ...base,
        tag: {
          text: "Ready to bill",
          title: "The work is complete. Send the final invoice to bill it.",
        },
      };
    }
    if (invoice?.earlySentAt) {
      return {
        label: "Sent",
        variant: "info",
        tag: {
          text: "Early",
          title: opts.isStaff
            ? "Sent with the early-payment option on " + fmt(invoice.earlySentAt) +
              ". Not billed yet: the final invoice goes out when the work is complete."
            : "Sent with the option to pay early. Your final invoice will follow when the work is complete.",
        },
      };
    }
    return base;
  }

  if ((status === 2 || status === 3) && projectKnown && !workDone && !cancelled) {
    return {
      ...base,
      tag: {
        text: "Early",
        title: opts.isStaff
          ? "Paid before the work was completed. The final total can still change; check it at the final send."
          : "Paid early. Your final invoice will follow when the work is complete.",
      },
    };
  }

  return base;
}

const tipSx = {
  display: "none",
  position: "absolute" as const,
  top: "calc(100% + 6px)",
  left: 0,
  zIndex: 1000,
  width: "240px",
  padding: "8px 10px",
  borderRadius: "6px",
  backgroundColor: "#222",
  color: "#fff",
  fontFamily: "Poppins, sans-serif",
  fontSize: "11px",
  fontWeight: 400,
  lineHeight: "15px",
  letterSpacing: "normal",
  textTransform: "none" as const,
  textAlign: "left" as const,
  whiteSpace: "normal" as const,
  boxShadow: "0 4px 12px rgba(0,0,0,0.18)",
  pointerEvents: "none" as const,
};

/**
 * Shows `tip` immediately on hover or keyboard focus. The browser's own
 * `title` tooltip was too slow to notice (users saw only the help cursor).
 */
export function HoverTip({ tip, children }: { tip: string; children: React.ReactNode }) {
  return (
    <Box
      as="span"
      sx={{
        position: "relative",
        display: "inline-block",
        "&:hover > .gm-hover-tip, &:focus-within > .gm-hover-tip": { display: "block" },
      }}
    >
      <Box as="span" tabIndex={0} aria-label={tip} sx={{ display: "inline-block", outline: "none" }}>
        {children}
      </Box>
      <Box as="span" className="gm-hover-tip" role="tooltip" sx={tipSx}>
        {tip}
      </Box>
    </Box>
  );
}

const tagSx = {
  display: "inline-block",
  padding: "1px 6px",
  borderRadius: "8px",
  border: "1px solid #CAA11C",
  color: "#8A6D0F",
  backgroundColor: "#FFFAEA",
  fontFamily: "Poppins-Medium, Poppins, sans-serif",
  fontSize: "10px",
  lineHeight: "16px",
  letterSpacing: "0.3px",
  whiteSpace: "nowrap" as const,
  cursor: "default",
};

interface Props {
  invoice: any;
  /** Pass when the screen knows the project status but the invoice object does not carry it. */
  projectStatusName?: string | null;
}

/** Status pill + optional tag. Hover the tag for what it means. */
export default function InvoiceStatusBadge({ invoice, projectStatusName }: Props) {
  const { user } = useContext<any>(UserContext);
  const isStaff = user?.role !== null && user?.role !== undefined;
  const d = getInvoiceStatusDisplay(invoice, { isStaff, projectStatusName });
  return (
    <Flex as="span" sx={{ display: "inline-flex", alignItems: "center", gap: "6px", flexWrap: "wrap" }}>
      <Badge as="span" variant={d.variant}>
        {d.label}
      </Badge>
      {d.tag && (
        <HoverTip tip={d.tag.title}>
          <Box as="span" sx={tagSx}>
            {d.tag.text}
          </Box>
        </HoverTip>
      )}
    </Flex>
  );
}
